import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { MOTO_BRANDS, BIKE_BRANDS } from "../lib/listingConstants";

// Feb-2026 (User-Wunsch 24 May Iter 15) — Auto-Trigger der KI-Helfer für die
// CreateListingPage, ausgelagert aus CreateListingPage.jsx:
//   • KBA-Lookup → wenn HSN (4) + TSN (3-12) vorhanden
//   • Werks-Daten (ai-spec-lookup) → wenn Marke + Modell + Jahr vorhanden
//   • Titel → wenn Marke + Modell + Jahr + km vorhanden
//   • Hersteller (KBA/ZB1) → Marke übernehmen, solange Marke leer ist
// Jeder Trigger feuert maximal EINMAL pro Page-Lifetime (Refs). Nur leere
// Felder werden befüllt — User-Eingaben sind unantastbar.
export default function useListingAutofill({ form, setForm, isEdit, user, markAiFields, update }) {
    const kbaAutoTriggeredRef = useRef(false);
    const specAutoTriggeredRef = useRef(false);
    const titleAutoTriggeredRef = useRef(false);

    useEffect(() => {
        if (isEdit || !user) return;
        const hsn = (form.hsn || "").toString().trim();
        const tsn = (form.tsn || "").toString().trim();
        if (kbaAutoTriggeredRef.current) return;
        if (hsn.length !== 4 || tsn.length < 3 || tsn.length > 12) return;
        kbaAutoTriggeredRef.current = true;
        const t = setTimeout(async () => {
            try {
                const r = await api.post("/vehicles/kba-lookup", {
                    hsn,
                    tsn,
                    first_registration_date: form.first_registration_date || null,
                    // Feb-2026 Iter 28 (User-Bug): bereits aus ZB1 extrahierte Marke/Modell
                    // als Anti-Halluzinations-Anker mitschicken — verhindert z.B.
                    // dass Gemini bei unklarer HSN/TSN „Tesla" rät.
                    expected_brand: form.brand || null,
                    expected_model: form.model || null,
                });
                if (!r.data?.ok) {
                    if (r.data?.warning) {
                        // Halluzinations-Filter (low_confidence / brand_mismatch) → Hinweis
                        toast.warning(r.data.warning, { duration: 8000 });
                    }
                    return;
                }
                const d = r.data.data || {};
                const normBrand = d.brand
                    ? d.brand.charAt(0).toUpperCase() + d.brand.slice(1).toLowerCase()
                    : null;
                const mapped = {
                    brand: normBrand,
                    manufacturer: d.manufacturer,
                    model: d.model_variant,
                    vehicle_class: d.vehicle_class,
                    displacement: d.displacement,
                    power_kw: d.power_kw,
                    power_hp: d.power_hp,
                    max_speed_kmh: d.top_speed_kmh,
                    emission_class: d.emission_class,
                    fuel: d.fuel,
                };
                let applied = 0;
                const appliedKeys = [];
                setForm((p) => {
                    const next = { ...p };
                    Object.entries(mapped).forEach(([k, v]) => {
                        if (v == null || v === "") return;
                        if (p[k] === undefined || p[k] === null || p[k] === "") {
                            next[k] = typeof v === "number" ? String(v) : v;
                            applied += 1;
                            appliedKeys.push(k);
                        }
                    });
                    return next;
                });
                if (applied > 0) {
                    markAiFields(appliedKeys);
                    toast.success(`KI hat ${applied} KBA-Felder automatisch befüllt.`);
                }
            } catch {
                /* silent — auto-trigger darf nicht stören */
            }
        }, 800);
        return () => clearTimeout(t);
    }, [form.hsn, form.tsn, form.first_registration_date, isEdit, user]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isEdit || !user) return;
        if (specAutoTriggeredRef.current) return;
        if (!form.brand || !form.model) return;
        const year = form.first_registration_date
            ? parseInt(form.first_registration_date.slice(0, 4), 10)
            : (form.year ? Number(form.year) : null);
        if (!year) return;
        specAutoTriggeredRef.current = true;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/listings/ai-spec-lookup", {
                    brand: form.brand,
                    model: form.model,
                    year,
                    category: form.category || "motorcycle",
                });
                if (data.mocked) return;
                const specs = data.specs || {};
                let applied = 0;
                const appliedKeys = [];
                setForm((p) => {
                    const next = { ...p };
                    Object.entries(specs).forEach(([k, v]) => {
                        if (v == null || v === "") return;
                        if (p[k] === undefined || p[k] === null || p[k] === "") {
                            next[k] = typeof v === "number" ? String(v) : v;
                            applied += 1;
                            appliedKeys.push(k);
                        }
                    });
                    return next;
                });
                if (applied > 0) {
                    markAiFields(appliedKeys);
                    toast.success(`KI hat ${applied} Werks-Daten automatisch ergänzt.`);
                }
            } catch {
                /* silent */
            }
        }, 1000);
        return () => clearTimeout(t);
    }, [form.brand, form.model, form.first_registration_date, form.year, form.category, isEdit, user]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isEdit || !user) return;
        if (titleAutoTriggeredRef.current) return;
        if (form.title && form.title.length >= 5) return;
        if (!form.brand || !form.model) return;
        const year = form.first_registration_date
            ? new Date(form.first_registration_date).getFullYear()
            : form.year;
        const km = Number(form.mileage) || 0;
        if (!year || km <= 0) return;
        titleAutoTriggeredRef.current = true;
        const parts = [
            [form.brand, form.model].filter(Boolean).join(" "),
            year ? `EZ ${year}` : null,
            km > 0 ? `${km.toLocaleString("de-DE")} km` : null,
            form.condition === "Neufahrzeug" ? "Neu" : null,
        ].filter(Boolean);
        const suggested = parts.join(", ");
        if (suggested) {
            update("title", suggested);
            toast.success("KI hat den Titel automatisch erstellt.");
        }
    }, [form.brand, form.model, form.first_registration_date, form.year, form.mileage, form.condition, form.title, isEdit, user]); // eslint-disable-line react-hooks/exhaustive-deps

    // Feb-2026 (User-Wunsch) — KBA-/ZB1-„ermittelter Hersteller" automatisch in
    // das Marke-Feld übernehmen, solange die Marke noch leer ist. Der Inserent
    // kann die Marke jederzeit über die Marken-Combobox überschreiben (danach
    // greift dieser Effect nicht mehr, weil form.brand dann gesetzt ist).
    useEffect(() => {
        const mfr = (form.manufacturer || "").toString().trim();
        if (!mfr) return;
        if ((form.brand || "").toString().trim()) return;
        const tc = mfr.charAt(0).toUpperCase() + mfr.slice(1).toLowerCase();
        const all = form.category === "motorcycle" ? MOTO_BRANDS : BIKE_BRANDS;
        const match = all.find((b) => b.toLowerCase() === tc.toLowerCase()) || tc;
        setForm((p) => (p.brand && p.brand.trim() ? p : { ...p, brand: match }));
        markAiFields(["brand"]);
    }, [form.manufacturer, form.category]); // eslint-disable-line react-hooks/exhaustive-deps
}
